import { linkOptions } from '@tanstack/react-router';
import type { Citation } from '@/features/documents/types';
import { citationFromEvent } from '@/features/chat/citations';
import type { ArtifactDetail } from '@/features/artifacts/types';

const REVISION_GROUP_GAP = 10 * 60 * 1000;

export const groupRevisions = <
  T extends { revision: number; createdAt: string; createdBy: string | null; origin: string },
>(
  revisions: T[],
) =>
  revisions
    .toSorted((a, b) => b.revision - a.revision)
    .reduce<[T, ...T[]][]>((groups, entry) => {
      const group = groups.at(-1);
      const previous = group?.at(-1);
      const author = group?.find((item) => item.createdBy)?.createdBy;
      if (
        group &&
        previous &&
        (previous.origin === 'agent') === (entry.origin === 'agent') &&
        (!author || !entry.createdBy || author === entry.createdBy) &&
        Date.parse(previous.createdAt) - Date.parse(entry.createdAt) <= REVISION_GROUP_GAP
      )
        group.push(entry);
      else groups.push([entry]);
      return groups;
    }, []);

export const artifactCitations = (artifact?: ArtifactDetail): Citation[] =>
  (artifact?.citationSources ?? []).map((source, index) => ({
    ...citationFromEvent(source),
    postfix: index + 1,
    order: index + 1,
  }));

export const artifactLinks = ({
  projectId = '',
  workspaceId,
  artifactId = '',
}: {
  projectId?: string;
  workspaceId?: string;
  artifactId?: string;
}) => {
  if (workspaceId)
    return {
      detail: linkOptions({
        to: '/project/$projectId/workspaces/$workspaceId/artifacts/$artifactId',
        params: { projectId, workspaceId, artifactId },
      }),
      history: linkOptions({
        to: '/project/$projectId/workspaces/$workspaceId/artifacts/$artifactId/version-history',
        params: { projectId, workspaceId, artifactId },
      }),
    };
  return {
    detail: linkOptions({
      to: '/project/$projectId/artifacts/$artifactId',
      params: { projectId, artifactId },
    }),
    history: linkOptions({
      to: '/project/$projectId/artifacts/$artifactId/version-history',
      params: { projectId, artifactId },
    }),
  };
};

const BASE64_CHUNK = 0x8000;

export const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = '';
  for (let start = 0; start < bytes.length; start += BASE64_CHUNK)
    binary += String.fromCharCode(...bytes.subarray(start, start + BASE64_CHUNK));
  return btoa(binary);
};

export const gzipBytes = async (bytes: Uint8Array) =>
  new Uint8Array(
    await new Response(
      new Blob([bytes.slice()]).stream().pipeThrough(new CompressionStream('gzip')),
    ).arrayBuffer(),
  );
